import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES, ICONS_PALETTE } from '_constants/icons';
import { ROUTES } from '_constants/routes';
import { Merchant_Processing_FAQ } from '_constants/faq';

import { Divider } from '_components/atomic/layouts';
import Circle from '_components/atomic/Circle';
import Dots from '_components/atomic/Dots';
import FancyList from '_components/atomic/FancyList';
import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import HowSection from '_components/composed/sections/HowSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import BottomNavigationSection from '_components/composed/sections/BottomNavigationSection';
import FAQSection from '_components/composed/sections/FAQSection';
import ProcessSection from '_components/composed/sections/ProcessSection';
import ProductBannerSection from '_components/composed/sections/ProductBannerSection';

const { colors, spaces } = theme;

const StyledMerchantProcessingPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  .link-container {
    width: 100%;
  }
`;

const MerchantProcessingPage = () => {
  return (
    <StyledMerchantProcessingPage>
      <ProductBannerSection
        title="Merchant Processing"
        text="Merchant processing is an essential part of running a business. It’s how businesses receive and manage payments from customers. It involves connecting a merchant account to a payment processor which is linked to card networks like Visa and Mastercard. This allows businesses to securely process transactions and keep track of their finances. Having a good merchant processing solution is important for any business to succeed."
        buttonsBlock={
          <div className="link-container">
            <Link href={config.merchantUrl}>
              <Button
                suffix={
                  <Icon
                    alias={ICONS_ALIASES.arrowRight}
                    color={colors.indigo[600]}
                  />
                }
                color={colors.white}
                textColor={colors.indigo[600]}
                hoverColor={colors.white}
                hoverTextColor={colors.indigo[600]}
              >
                Get Approved for Payment Processing Now
              </Button>
            </Link>
          </div>
        }
        backgroundImage={`${config.basePath}/images/merchant-processing-banner-background.svg`}
        desktopImage={`${config.basePath}/images/merchant-processing-banner-desktop.png`}
        mobileImage={`${config.basePath}/images/merchant-processing-banner-mobile.png`}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <ProcessSection
        title="How does Merchant Processing work?"
        activeStepIdx={0}
        steps={[
          {
            title: 'STEP 01',
            subtitle: 'Customer Purchases',
            description: (
              <span>
                The customer slides their payment card through the credit card
                terminal and enters the sale amount.
              </span>
            ),
            imageSrc: `${config.basePath}/images/cash-payment.svg`,
            reverse: false,
          },
          {
            title: 'STEP 02',
            subtitle: 'Payment Gateway',
            description: (
              <span>
                The terminal securely connects to our payment gateway for
                authorization.
              </span>
            ),
            imageSrc: `${config.basePath}/images/credit-card-payment.svg`,
            reverse: true,
          },
          {
            title: 'STEP 03',
            subtitle: 'Payment Processor',
            description: (
              <span>
                Our payment gateway passes the transaction data to the bank that
                issued the credit card to check if the card is valid and if the
                charge amount is available.
              </span>
            ),
            imageSrc: `${config.basePath}/images/online-transactions.svg`,
            reverse: false,
          },
          {
            title: 'STEP 04',
            subtitle: 'Bank',
            description: (
              <span>
                The issuing bank sends back an approval number or a decline
                message, which is processed by our payment gateway. This
                information is then sent back to the credit card terminal to
                print a receipt for the customer to sign.
              </span>
            ),
            imageSrc: `${config.basePath}/images/e-wallet.svg`,
            reverse: true,
          },
          {
            title: 'STEP 05',
            subtitle: 'Your Merchant Account',
            description: (
              <span>
                The transaction is finalized, and the funds are deposited into
                your merchant account, completing the payment process.
                <br />
                These steps together typically take around 12-15 seconds to
                complete using a phone line, and only a few seconds with newer
                internet-enabled machines.
              </span>
            ),
            imageSrc: `${config.basePath}/images/finance-leaders.svg`,
            reverse: false,
          },
        ]}
      />

      <HowSection
        title={<>What makes a good merchant processor?</>}
        rightPart={
          <FancyList
            items={[
              {
                title: 'Increased security',
                description:
                  'Offers advanced security measures to protect against fraud and security breaches.',
                icon: ICONS_ALIASES.security,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.security],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
              {
                title: 'Improved customer experience',
                description:
                  'Provides fast and seamless payment processing for a positive customer experience.',
                icon: ICONS_ALIASES.happy,
                iconPalette: ICONS_PALETTE.green[ICONS_ALIASES.happy],
                color: colors.green[50],
                textColor: colors.green[500],
              },
              {
                title: 'Enhanced reporting and tracking',
                description:
                  'Offers detailed reporting and tracking capabilities for easy transaction management.',
                icon: ICONS_ALIASES.creditCard,
                iconPalette: ICONS_PALETTE.blue[ICONS_ALIASES.creditCard],
                color: colors.blue[25],
                textColor: colors.blue[500],
              },
              {
                title: 'Reduced processing costs',
                description:
                  'Lowers fees and helps avoid costs associated with fraud and security breaches.',
                icon: ICONS_ALIASES.reduceCost,
                iconPalette: ICONS_PALETTE.orange[ICONS_ALIASES.reduceCost],
                color: colors.orange[50],
                textColor: colors.orange[500],
              },
              {
                title: 'Increased convenience',
                description:
                  'Offers flexible payment options, including online and mobile payments.',
                icon: ICONS_ALIASES.adaptation,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.adaptation],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
              {
                title: 'Global reach',
                description:
                  'Enables businesses to accept payments from customers in different countries and currencies.',
                icon: ICONS_ALIASES.globalNetwork,
                iconPalette: ICONS_PALETTE.green[ICONS_ALIASES.globalNetwork],
                color: colors.green[50],
                textColor: colors.green[500],
              },
              {
                title: 'Improved cash flow',
                description:
                  'Provides fast and efficient payment processing for quicker payment receipt and improved cash flow.',
                icon: ICONS_ALIASES.cashFlow,
                iconPalette: ICONS_PALETTE.blue[ICONS_ALIASES.cashFlow],
                color: colors.blue[25],
                textColor: colors.blue[500],
              },
            ]}
          />
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomBannerSection
        header={<>Get Approved for Payment Processing</>}
        footer={
          <>
            <div className="buttonsContainer">
              <Link href={config.merchantUrl}>
                <Button
                  suffix={
                    <Icon
                      alias={ICONS_ALIASES.arrowRight}
                      color={colors.indigo[600]}
                    />
                  }
                  color={colors.white}
                  textColor={colors.indigo[600]}
                  hoverColor={colors.white}
                  hoverTextColor={colors.indigo[600]}
                >
                  Get Approved for Payment Processing Now
                </Button>
              </Link>
            </div>
          </>
        }
        moreSpace
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <FAQSection items={Merchant_Processing_FAQ} />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomNavigationSection
        previousService={{
          title: 'Registered Agent',
          link: ROUTES.registeredAgent,
        }}
        nextService={{
          title: 'LLC Incorporation',
          link: ROUTES.llcIncorporation,
        }}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(49% + 4px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={-90}
        left={49}
        top={`calc(45% - 10px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={260}
        top={`calc(61% - 20px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-172}
        top={`calc(51% + 40px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(83% + 10px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={90}
        left={-134}
        top={`calc(79% - 4px)`}
      />
    </StyledMerchantProcessingPage>
  );
};

export default MerchantProcessingPage;
